import React from "react";
import { formatDate } from "../../utils/adminFormat";
import {
  Table, TableHead, TableBody, TableRow, TableCell, Chip,
  Box, Stack, Typography, Link as MuiLink, useTheme, useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";

const STATUS_CHIP = {
  active: { label: "Active", bg: "#dcfce7", fg: "#166534" },
  trial: { label: "Trial", bg: "#dbeafe", fg: "#1d4ed8" },
  trial_ended_pending: { label: "Trial ended", bg: "#fef3c7", fg: "#b45309" },
  past_due: { label: "Past due", bg: "#fee2e2", fg: "#b91c1c" },
  cancelled: { label: "Cancelled", bg: "#fde2e2", fg: "#dc2626" },
  expired: { label: "Expired", bg: "#e5e7eb", fg: "#4b5563" },
  none: { label: "No plan", bg: "#e5e7eb", fg: "#374151" },
};

const statusChip = (status) => {
  const cfg = STATUS_CHIP[String(status || "none").toLowerCase()] || STATUS_CHIP.none;
  return (
    <Chip size="small" label={cfg.label} sx={{ bgcolor: cfg.bg, color: cfg.fg, fontWeight: 600 }} />
  );
};

const userLink = (row) => (
  <MuiLink
    component={Link}
    to={`/admin/mobile-users/${row.id}`}
    underline="hover"
    sx={{ color: "#7c3aed", fontWeight: 700 }}
  >
    {row.name || row.email || `#${row.id}`}
  </MuiLink>
);

export default function RecentAppUsersTable({ rows }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const data = rows || [];

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>Recent App Users</Typography>
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: "#4B5563", background: "#F3F4F6", borderRadius: 999, px: 1, py: 0.25, ml: 0.75, whiteSpace: "nowrap" }}>Live</Typography>
      </Stack>
      <MuiLink
        component={Link}
        to="/admin/mobile-users"
        underline="hover"
        sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
      >
        View all
      </MuiLink>
    </Stack>
  );

  if (isMobile) {
    return (
      <Box>
        {header}
        <Stack spacing={1}>
          {data.map((r) => (
            <Box key={r.id} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1 }}>
              <Box>{userLink(r)}</Box>
              <Typography variant="caption" sx={{ color: "#9aa1ac" }}>{r.email}</Typography>
              <Typography variant="caption" display="block" sx={{ color: "#374151", fontWeight: 700 }}>
                {r.plan || "No plan"} · Joined {formatDate(r.joined_at)} · Last payment {formatDate(r.last_payment_at)}
              </Typography>
              <Box mt={0.5}>{statusChip(r.status)}</Box>
            </Box>
          ))}
          {data.length === 0 && <Typography variant="body2" sx={{ color: "#374151" }}>No data</Typography>}
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Plan</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Joined</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Last Payment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>
                {userLink(r)}
                <Typography variant="caption" display="block" sx={{ color: "#9aa1ac" }}>{r.email}</Typography>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151" }}>{r.plan || "—"}</TableCell>
              <TableCell>{statusChip(r.status)}</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151" }}>{formatDate(r.joined_at)}</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151" }}>{formatDate(r.last_payment_at)}</TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center">No data</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      </Box>
    </Box>
  );
}
